import React, { useState } from 'react';
import GB from 'country-flag-icons/react/3x2/GB';
import IR from 'country-flag-icons/react/3x2/IR';
import './ServerManagement.css';

function ServerManagement({ server, onBack, authToken, onTokenExpiration }) {
  const [licenseRevealed, setLicenseRevealed] = useState(false);
  const [currentLicense, setCurrentLicense] = useState(server.license);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isTrashing, setIsTrashing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'revoke' or 'trash'

  // Vanity ID state
  const [currentVanity, setCurrentVanity] = useState(server.vanity_id || '');
  const [vanityActive, setVanityActive] = useState(!!server.vanity_active);
  const [vanityCooldownUntil, setVanityCooldownUntil] = useState(server.vanity_cooldown_until || 0);
  const [vanityInput, setVanityInput] = useState('');
  const [isSettingVanity, setIsSettingVanity] = useState(false);
  const [vanityMsg, setVanityMsg] = useState(null); // { type: 'success' | 'error', text }
  const [vanityLang, setVanityLang] = useState('en'); // 'en' | 'fa' — language toggle for this section

  const boostCount = server.boost_count ?? 0;
  const hasEnoughBoosts = boostCount >= 50;
  const nowSec = Math.floor(Date.now() / 1000);
  const cooldownRemaining = Math.max(0, vanityCooldownUntil - nowSec);
  const cooldownHours = Math.ceil(cooldownRemaining / 3600);

  // Countdown: when do active boosts fall below 50? (0 = already below 50)
  const boostBelow50At = server.boost_below_50_at || 0;
  const below50Days = boostBelow50At > 0 ? Math.ceil(Math.max(0, boostBelow50At - nowSec) / 86400) : 0;
  const boostDropAmount = server.boost_drop_amount || 0; // boosts expiring on that day
  const boostAfterDrop = server.boost_after_drop || 0;   // count left right after

  // Persian (Farsi) digit helper for the bilingual labels
  const faNum = (n) => String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
  const connectId = currentVanity || server.id_2;
  const fa = vanityLang === 'fa'; // active language for the vanity section

  const displayName = server.label === 'NO NAME' ? `Server ${server.id_2}` : server.label;

  const handleLicenseClick = () => {
    setLicenseRevealed(true);
  };

  const handleCopyLicense = async () => {
    try {
      await navigator.clipboard.writeText(currentLicense);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy license:', err);
      setError('Failed to copy license to clipboard');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleRevokeLicense = async () => {
    try {
      setIsRevoking(true);
      setError(null);
      setShowConfirmation(false);
      
      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=regenerateLicense&license=${currentLicense}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      
      if (data && data.trim()) {
        setCurrentLicense(data.trim());
        setLicenseRevealed(true); // Auto-reveal the new license
      } else {
        throw new Error('Invalid license received from server');
      }
    } catch (error) {
      console.error('Failed to revoke license:', error);
      setError(error.message);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleTrashLicense = async () => {
    try {
      setIsTrashing(true);
      setError(null);
      setShowConfirmation(false);
      
      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=trashLicense&license=${currentLicense}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // After successful trash, redirect back to main page
      onBack();
    } catch (error) {
      console.error('Failed to trash license:', error);
      setError(error.message);
      setIsTrashing(false);
    }
  };

  const handleRevokeClick = () => {
    setConfirmationType('revoke');
    setShowConfirmation(true);
  };

  const handleTrashClick = () => {
    setConfirmationType('trash');
    setShowConfirmation(true);
  };

  const handleConfirmAction = () => {
    if (confirmationType === 'revoke') {
      handleRevokeLicense();
    } else if (confirmationType === 'trash') {
      handleTrashLicense();
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationType(null);
  };

  const handleVanityInputChange = (e) => {
    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5);
    setVanityInput(cleaned);
    if (vanityMsg) setVanityMsg(null);
  };

  const handleSetVanity = async () => {
    if (vanityInput.length !== 5 || isSettingVanity) return;
    try {
      setIsSettingVanity(true);
      setVanityMsg(null);

      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=setVanity&license=${encodeURIComponent(currentLicense)}&vanity=${encodeURIComponent(vanityInput)}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }

      if (response.ok && data.status === 1) {
        setCurrentVanity(data.vanity_id || vanityInput);
        setVanityActive(!!data.live);
        setVanityCooldownUntil(Math.floor(Date.now() / 1000) + 48 * 3600);
        setVanityInput('');
        setVanityMsg({ type: 'success', text: data.message || 'Vanity ID saved.' });
      } else {
        setVanityMsg({ type: 'error', text: data.message || `Failed to set vanity ID (status ${response.status}).` });
      }
    } catch (error) {
      console.error('Failed to set vanity:', error);
      setVanityMsg({ type: 'error', text: error.message });
    } finally {
      setIsSettingVanity(false);
    }
  };

  return (
    <div className="server-management">
      <div className="management-header">
        <button className="back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Servers
        </button>
        <div className="server-title">
          <h2>{displayName}</h2>
          <span className="server-id">ID: {server.id_2}</span>
        </div>
      </div>

      <div className="management-content">
        <div className="license-section">
          <div className="section-header">
            <h3>Server License</h3>
            <p>Click on the license key to reveal it</p>
            {error && (
              <div className="error-message">
                <p>{error}</p>
              </div>
            )}
          </div>
          
          <div className="license-container">
            <div 
              className={`license-display ${licenseRevealed ? 'revealed' : 'blurred'}`}
              onClick={handleLicenseClick}
            >
              <div className="license-label">License Key:</div>
              <div className="license-value">
                {currentLicense}
              </div>
              {!licenseRevealed && (
                <div className="click-hint">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Click to reveal
                </div>
              )}
            </div>
            
            {licenseRevealed && (
              <div className="license-actions">
                <button 
                  className={`action-btn copy-btn ${copySuccess ? 'success' : ''}`}
                  onClick={handleCopyLicense}
                  disabled={isRevoking || isTrashing}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {copySuccess ? (
                      <path d="M20 6L9 17l-5-5"/>
                    ) : (
                      <>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                      </>
                    )}
                  </svg>
                  {copySuccess ? 'Copied!' : 'Copy'}
                </button>
                
                <button 
                  className="action-btn revoke-btn"
                  onClick={handleRevokeClick}
                  disabled={isRevoking || isTrashing}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18"/>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                  {isRevoking ? 'Revoking...' : 'Revoke'}
                </button>
                
                <button 
                  className="action-btn trash-btn"
                  onClick={handleTrashClick}
                  disabled={isRevoking || isTrashing}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="M19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                  {isTrashing ? 'Trashing...' : 'Trash'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`vanity-section ${fa ? 'lang-fa' : ''}`}>
          <div className="vanity-lang-switch">
            <button
              type="button"
              className={`vanity-flag ${!fa ? 'active' : ''}`}
              onClick={() => setVanityLang('en')}
              title="English"
              aria-label="English"
            >
              <GB title="English" />
            </button>
            <button
              type="button"
              className={`vanity-flag ${fa ? 'active' : ''}`}
              onClick={() => setVanityLang('fa')}
              title="فارسی"
              aria-label="Farsi"
            >
              <IR title="فارسی" />
            </button>
          </div>

          <div className="section-header">
            <h3>{fa ? 'آیدی اختصاصی سرور' : 'Vanity Server ID'}</h3>
            <p>{fa ? 'یک آیدی دلخواه ۵ کاراکتری برای سرورتان انتخاب کنید.' : 'Pick a custom 5-character ID for your server.'}</p>
          </div>

          <div className="vanity-ids">
            <div className="vanity-id-block">
              <span className="vanity-id-caption">{fa ? 'آیدی اصلی' : 'Original ID'}</span>
              <div className="vanity-id-value original" dir="ltr">{server.id_2}</div>
              <span className="vanity-id-note">{fa ? 'همیشه برای اتصال کار می‌کند' : 'Always works for connecting'}</span>
            </div>
            <div className="vanity-id-block">
              <span className="vanity-id-caption">{fa ? 'آیدی اختصاصی' : 'Vanity ID'}</span>
              {currentVanity ? (
                <>
                  <div className="vanity-id-value vanity" dir="ltr">{currentVanity}</div>
                  <span className={`vanity-badge ${vanityActive ? 'active' : 'reserved'}`}>
                    {vanityActive ? (fa ? 'فعال' : 'Active') : (fa ? 'رزرو شده' : 'Reserved')}
                  </span>
                </>
              ) : (
                <div className="vanity-id-value none">{fa ? 'تنظیم نشده' : 'Not set'}</div>
              )}
            </div>
          </div>

          <div className="vanity-connect-note">
            {fa
              ? <>می‌توانید با هر کدام از آیدی‌ها وصل شوید — مثلاً <code dir="ltr">connect {connectId}</code></>
              : <>You can connect using either ID — for example <code dir="ltr">connect {connectId}</code></>}
          </div>

          <ul className="vanity-rules">
            {fa ? (
              <>
                <li>حداقل <strong>۵۰ بوست فعال</strong> لازم است.</li>
                <li>دقیقاً <strong>۵ کاراکتر</strong> — حروف <code>a–z</code> و اعداد <code>0–9</code>.</li>
                <li>باید مرتبط با <strong>نام یا برند</strong> سرورتان باشد — آیدی‌های نامرتبط، گمراه‌کننده یا جعل هویت ممکن است بعداً باطل شوند.</li>
                <li>هر <strong>۴۸ ساعت</strong> یک‌بار قابل تغییر است.</li>
                <li>تا وقتی <strong>۵۰+ بوست</strong> داشته باشید مال شماست (حتی اگر سرور روشن نباشد). اگر بوست‌هایتان زیر ۵۰ بیاید، <strong>۳ ماه</strong> فرصت دارید دوباره شارژ کنید وگرنه برای دیگران آزاد می‌شود.</li>
                <li>آیدی تصادفی اصلی همیشه برای اتصال کار می‌کند، پس لینک‌های قدیمی خراب نمی‌شوند.</li>
              </>
            ) : (
              <>
                <li>Requires at least <strong>50 active boosts</strong>.</li>
                <li>Exactly <strong>5 characters</strong> — letters <code>a–z</code> and numbers <code>0–9</code>.</li>
                <li>Must relate to your server's <strong>name or brand</strong> — unrelated, misleading, or impersonating IDs may be revoked later.</li>
                <li>Can be changed <strong>once every 48 hours</strong>.</li>
                <li>It stays yours while you keep <strong>50+ boosts</strong> (even if the server isn't running). If your boosts drop below 50, you have <strong>3 months</strong> to top up again before it's released to others.</li>
                <li>Your original random ID keeps working for connecting, so old links never break.</li>
              </>
            )}
          </ul>

          {!hasEnoughBoosts && (
            <div className="vanity-locked">
              {fa ? (
                <>
                  <span>🔒 شما <strong>{faNum(boostCount)}</strong> از ۵۰ بوست دارید.</span>
                  {currentVanity
                    ? <span> آیدی شما رزرو است — برای فعال‌سازی یا تغییر به ۵۰ بوست برسید.</span>
                    : <span> برای باز کردن آیدی اختصاصی به ۵۰ بوست برسید.</span>}
                </>
              ) : (
                <>
                  <span>🔒 You have <strong>{boostCount}</strong> / 50 boosts.</span>
                  {currentVanity
                    ? <span> Your vanity is reserved — reach 50 boosts to reactivate or change it.</span>
                    : <span> Reach 50 boosts to unlock a vanity ID.</span>}
                </>
              )}
            </div>
          )}

          {boostBelow50At > 0 && (
            <div className={`vanity-boost-countdown ${below50Days <= 14 ? 'warn' : ''}`}>
              {fa ? (
                <>
                  ⏳ در حال حاضر <strong>{faNum(boostCount)}</strong> بوست فعال دارید. حدود <strong>{faNum(below50Days)}</strong> روز دیگر (حوالی {new Date(boostBelow50At * 1000).toLocaleDateString('fa-IR')}) تعداد <strong>{faNum(boostDropAmount)}</strong> بوست منقضی می‌شود و بوست فعالتان به <strong>{faNum(boostAfterDrop)}</strong> کاهش می‌یابد — یعنی زیر حد ۵۰.
                  {currentVanity ? ' برای اینکه آیدی اختصاصی‌تان فعال بماند، پیش از آن تاریخ بوست کنید.' : ''}
                </>
              ) : (
                <>
                  ⏳ You currently have <strong>{boostCount}</strong> active boosts. In about <strong>{below50Days}</strong> day{below50Days === 1 ? '' : 's'} (around {new Date(boostBelow50At * 1000).toLocaleDateString()}), <strong>{boostDropAmount}</strong> of them expire and your active boosts drop to <strong>{boostAfterDrop}</strong> — below the 50 threshold.
                  {currentVanity ? ' Add more boosts before then to keep your vanity ID active.' : ''}
                </>
              )}
            </div>
          )}

          <div className="vanity-input-row">
            <input
              type="text"
              className="vanity-input"
              dir="ltr"
              placeholder={fa ? 'مثلاً abcd1' : 'abcd1'}
              value={vanityInput}
              onChange={handleVanityInputChange}
              maxLength={5}
              disabled={!hasEnoughBoosts || cooldownRemaining > 0 || isSettingVanity}
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
            />
            <button
              className="action-btn vanity-set-btn"
              onClick={handleSetVanity}
              disabled={!hasEnoughBoosts || cooldownRemaining > 0 || isSettingVanity || vanityInput.length !== 5 || vanityInput === currentVanity}
            >
              {isSettingVanity ? (fa ? 'در حال ذخیره...' : 'Saving...') : (currentVanity ? (fa ? 'تغییر' : 'Change') : (fa ? 'ثبت' : 'Set'))}
            </button>
          </div>

          {cooldownRemaining > 0 && (
            <div className="vanity-hint">
              {fa
                ? `⏱️ حدود ${faNum(cooldownHours)} ساعت دیگر می‌توانید آیدی اختصاصی را تغییر دهید.`
                : `⏱️ You can change your vanity ID again in about ${cooldownHours} hour(s).`}
            </div>
          )}

          {vanityMsg && (
            <div className={`vanity-message ${vanityMsg.type}`}>
              <p>{vanityMsg.text}</p>
            </div>
          )}
        </div>

        {showConfirmation && (
          <div className="confirmation-overlay">
            <div className="confirmation-dialog">
              <div className="confirmation-header">
                <h3>{confirmationType === 'revoke' ? 'Revoke License' : 'Trash License'}</h3>
              </div>
              <div className="confirmation-content">
                {confirmationType === 'revoke' ? (
                  <>
                    <p>Are you sure you want to revoke this license? This action will generate a new license key and invalidate the current one.</p>
                    <p><strong>This action cannot be undone.</strong></p>
                  </>
                ) : (
                  <>
                    <p>Are you sure you want to trash this license? This will permanently delete the server and cannot be undone.</p>
                    <p><strong>You will lose all server data and configuration.</strong></p>
                  </>
                )}
              </div>
              <div className="confirmation-actions">
                <button 
                  className="action-btn cancel-btn"
                  onClick={handleCancelAction}
                >
                  Cancel
                </button>
                <button 
                  className={`action-btn ${confirmationType === 'revoke' ? 'confirm-btn' : 'trash-confirm-btn'}`}
                  onClick={handleConfirmAction}
                >
                  {confirmationType === 'revoke' ? 'Yes, Revoke License' : 'Yes, Trash Server'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="server-info-section">
          <div className="section-header">
            <h3>Server Information</h3>
          </div>
          
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Server ID</span>
              <span className="info-value">{server.id_2}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Label</span>
              <span className="info-value">{server.label}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type</span>
              <span className="info-value">{server.type === 0 ? 'Standard' : 'Premium'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">IP Address</span>
              <span className="info-value">{server.ip || 'Not assigned'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Started</span>
              <span className="info-value">
                {server.last_start === 0 ? 'Never' : new Date(server.last_start * 1000).toLocaleString()}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className="info-value">
                {server.trash === 1 ? 'Trashed' : 
                 server.online === 1 ? 'Online' : 
                 server.last_start === 0 ? 'Not Started' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServerManagement;